import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import styles from './VerbFlowView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import recognitionLangs from '../speech/recognitionLangs'
import languages from '../languages'
import { celebrate } from './juice'
import { tokenizeLine, tokenizeTranscript, tokenMatches, romanizationOf, cancelTts } from '../speech/flowMatch'

import CloseIcon from '@mui/icons-material/Close'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import ReplayIcon from '@mui/icons-material/Replay'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

// Broad, language-agnostic tenses — the model does the real conjugation.
const TENSES = [
  { key: 'present', label: 'Present', promptLabel: 'present tense' },
  { key: 'past', label: 'Past', promptLabel: 'simple past tense' },
  { key: 'future', label: 'Future', promptLabel: 'future tense' },
]

// The six persons the drill cycles through. `key` is empty for "mixed".
const PERSONS = [
  { key: '', label: 'All persons (mixed)' },
  { key: 'I', label: 'I' },
  { key: 'you (singular)', label: 'you (singular)' },
  { key: 'he/she', label: 'he / she' },
  { key: 'we', label: 'we' },
  { key: 'you (plural)', label: 'you (plural)' },
  { key: 'they', label: 'they' },
]

const DEFAULT_COUNT = 10
const HINT_AFTER = 3    // failed attempts before we show a hint
const REVEAL_AFTER = 9  // failed attempts before we reveal the answer (3 + 6)

const vocabOf = (card) => {
  const t = card.translations?.[0]
  if (card.sourceLanguage && card.sourceLanguage !== 'eng' && card.source) {
    return { word: card.source, meaning: t?.target || '', lang: card.sourceLanguage }
  }
  if (t?.target && t.targetLanguage !== 'eng') {
    return { word: t.target, meaning: card.source || '', lang: t.targetLanguage }
  }
  return null
}

const parseItems = (raw) => {
  if (!raw) return []
  let text = String(raw).replace(/```json/gi, '').replace(/```/g, '').trim()
  const start = text.indexOf('{')
  const end = text.lastIndexOf('}')
  if (start !== -1 && end !== -1) text = text.slice(start, end + 1)
  try {
    const obj = JSON.parse(text)
    return Array.isArray(obj?.items) ? obj.items : []
  } catch {
    return []
  }
}

// Normalize a model item into a clean drill challenge. Guarantees stem+ending
// reconstruct the conjugated form (falls back to a whole-word blank).
const shapeItem = (raw, lang) => {
  const conjugated = String(raw?.conjugated || '').trim()
  if (!conjugated) return null
  let stem = String(raw?.stem || '').trim()
  let ending = String(raw?.ending || '').trim()
  if (!ending || (stem + ending).toLowerCase() !== conjugated.toLowerCase()) {
    // Trust the model's split only if it reconstructs; otherwise blank it all.
    if (conjugated.toLowerCase().startsWith(stem.toLowerCase()) && stem && stem.length < conjugated.length) {
      ending = conjugated.slice(stem.length)
    } else {
      stem = ''
      ending = conjugated
    }
  }
  return {
    verb: String(raw?.verb || '').trim(),
    subjectEnglish: String(raw?.subjectEnglish || '').trim(),
    subjectNative: String(raw?.subjectNative || '').trim(),
    conjugated,
    stem,
    ending,
    sentence: String(raw?.sentence || '').trim(),
    english: String(raw?.english || '').trim(),
    hint: String(raw?.hint || '').trim(),
    targetTokens: tokenizeLine(conjugated, lang).map(t => t.norm),
  }
}

export default function VerbFlowView({ cards, voiceSpeed, onClose }) {
  const { targetLang, verbs } = useMemo(() => {
    const v = (cards || []).map(vocabOf).filter(Boolean)
    const counts = {}
    v.forEach(x => { counts[x.lang] = (counts[x.lang] || 0) + 1 })
    const lang = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'eng'
    const inLang = v.filter(x => x.lang === lang)
    const flagged = inLang.filter(x => /^to\s+\S/i.test((x.meaning || '').trim()))
    return { targetLang: lang, verbs: flagged.length >= 3 ? flagged : inLang }
  }, [cards])
  const langName = languages[targetLang] || targetLang

  // ── setup ──
  const [tense, setTense] = useState('present')
  const [person, setPerson] = useState('')
  const [focusVerb, setFocusVerb] = useState('')
  const [count, setCount] = useState(DEFAULT_COUNT)
  const [phase, setPhase] = useState('setup') // 'setup' | 'loading' | 'drill' | 'error'
  const [errorMsg, setErrorMsg] = useState('')
  const [regenerating, setRegenerating] = useState(false)

  // ── drill ──
  const [items, setItems] = useState([])
  const [idx, setIdx] = useState(0)
  const [setNum, setSetNum] = useState(1)
  const [hits, setHits] = useState({ forIdx: -1, states: [] }) // per target-token: bool
  const [attempts, setAttempts] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [micError, setMicError] = useState(null)
  const [showRoman, setShowRoman] = useState(true)

  const item = items[idx]

  // recognizer plumbing (ported from ReadingFlowView)
  const recRef = useRef(null)
  const listeningRef = useRef(false)
  const completedRef = useRef(false)
  const sessionRef = useRef(0)
  const deadSessionsRef = useRef(0)
  const advanceTimerRef = useRef(null)
  const attemptsRef = useRef(0)
  const lastFinalRef = useRef('')
  const itemRef = useRef(null)
  useEffect(() => { itemRef.current = item }, [item])

  const serverUrl = process.env.REACT_APP_OLLAMA_URI
  const apiKey = process.env.REACT_APP_OLLAMA_KEY

  // ── generation ──────────────────────────────────────────────────────────
  const generate = useCallback(async (isRegen = false) => {
    if (!serverUrl) { setErrorMsg('Mac Studio (REACT_APP_OLLAMA_URI) is not configured.'); setPhase('error'); return }
    if (verbs.length === 0) { setErrorMsg('No verbs found in this class vocabulary.'); setPhase('error'); return }
    if (isRegen) setRegenerating(true); else setPhase('loading')
    setErrorMsg('')

    const tenseLabel = TENSES.find(t => t.key === tense)?.promptLabel || 'present tense'
    const verbLines = verbs.map(x => `- ${x.word} = ${x.meaning}`).join('\n')
    const personRule = person
      ? `Every item must use the subject: ${person}.`
      : `Vary the subject across the set (I, you (singular), he/she, we, you (plural), they).`
    const verbRule = focusVerb
      ? `Use the verb "${focusVerb}" in every item.`
      : `Use a variety of the verbs above — a different verb per item where possible.`

    const prompt = `You are a ${langName} teacher creating a SPOKEN verb-conjugation drill.

Verbs available (dictionary form = English meaning):
${verbLines}

Create ${count} drill items practising conjugation in the ${tenseLabel}.
${personRule}
${verbRule}

For each item provide:
- verb: the dictionary form used (from the list)
- subjectEnglish: one of "I","you (singular)","he/she","we","you (plural)","they"
- subjectNative: the ${langName} subject pronoun for that person
- conjugated: ONLY the verb, correctly conjugated for that subject in the ${tenseLabel} (no pronoun)
- stem: the unchanging leading part of "conjugated" (may be empty string)
- ending: the remaining part, so that stem + ending EXACTLY equals conjugated
- sentence: a short natural ${langName} sentence (3-7 words) that uses that exact conjugated verb
- english: the English translation of the sentence
- hint: ONE short English hint about how to form the ending — do NOT state the full answer

Return ONLY minified JSON, no markdown, exactly:
{"items":[{"verb":"","subjectEnglish":"","subjectNative":"","conjugated":"","stem":"","ending":"","sentence":"","english":"","hint":""}]}`

    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({
          model: 'qwen2.5:14b',
          prompt,
          stream: false,
          format: 'json',
          options: { temperature: 0.6, num_predict: 1600 },
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const built = parseItems(data.response).map(r => shapeItem(r, targetLang)).filter(Boolean)
      if (built.length === 0) { setErrorMsg('The model returned no usable items — try again.'); setPhase('error'); setRegenerating(false); return }
      setItems(built)
      setIdx(0)
      setSetNum(n => (isRegen ? n + 1 : 1))
      setRegenerating(false)
      setPhase('drill')
    } catch (e) {
      setErrorMsg(e.message || 'Generation failed.')
      setPhase('error')
      setRegenerating(false)
    }
  }, [serverUrl, apiKey, verbs, person, focusVerb, tense, count, langName, targetLang])

  // Reset per-item state whenever the challenge changes.
  useEffect(() => {
    if (phase !== 'drill' || !item) return
    setHits({ forIdx: idx, states: item.targetTokens.map(() => false) })
    setAttempts(0)
    setRevealed(false)
    setInterim('')
    completedRef.current = false
    attemptsRef.current = 0
    lastFinalRef.current = ''
  }, [idx, phase, item])

  const states = hits.forIdx === idx ? hits.states : []

  // ── advance / regenerate ────────────────────────────────────────────────
  const advance = useCallback(() => {
    clearTimeout(advanceTimerRef.current)
    setIdx(prev => {
      const next = prev + 1
      if (next >= items.length) {
        // Finished the set — automatically build the next one.
        generate(true)
        return prev
      }
      return next
    })
  }, [items.length, generate])

  // All target tokens hit → celebrate → next.
  useEffect(() => {
    if (hits.forIdx !== idx || !item) return
    if (item.targetTokens.length === 0 || hits.states.length !== item.targetTokens.length) return
    if (!hits.states.every(Boolean) || completedRef.current) return
    completedRef.current = true
    celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 26, spread: 150 })
    advanceTimerRef.current = setTimeout(advance, 1100)
  }, [hits, idx, item, advance])

  const applyTranscript = useCallback((transcript, isFinal) => {
    const cur = itemRef.current
    if (!cur) return
    const tokens = tokenizeTranscript(transcript, targetLang)
    setHits(prev => {
      if (prev.forIdx !== idx) return prev
      const next = [...prev.states]
      // The conjugated verb can appear anywhere in what they said — scan freely.
      for (let ti = 0; ti < cur.targetTokens.length; ti++) {
        if (next[ti]) continue
        if (tokens.some(tok => tokenMatches(tok, cur.targetTokens[ti]))) next[ti] = true
      }
      return { forIdx: prev.forIdx, states: next }
    })

    if (isFinal && !completedRef.current) {
      const heard = transcript.trim()
      if (heard && heard !== lastFinalRef.current) {
        lastFinalRef.current = heard
        attemptsRef.current += 1
        setAttempts(attemptsRef.current)
        if (attemptsRef.current >= REVEAL_AFTER) setRevealed(true)
      }
    }
  }, [idx, targetLang])

  // ── recognizer (ported) ─────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    listeningRef.current = false
    setListening(false)
    sessionRef.current++
    try { recRef.current?.stop() } catch { /* already stopped */ }
  }, [])

  const startListening = useCallback(() => {
    if (!SpeechRecognition) { setMicError('Speech recognition is not supported in this browser.'); return }
    if (!itemRef.current) return
    setMicError(null)
    cancelTts()
    const session = ++sessionRef.current
    const rec = new SpeechRecognition()
    rec.lang = recognitionLangs[targetLang] || 'en-US'
    rec.continuous = true
    rec.interimResults = true
    let heardSound = false

    const stopWithError = (msg) => { listeningRef.current = false; setListening(false); setMicError(msg) }

    rec.onsoundstart = () => { if (session === sessionRef.current) { heardSound = true; deadSessionsRef.current = 0 } }
    rec.onspeechstart = () => { if (session === sessionRef.current) { heardSound = true; deadSessionsRef.current = 0 } }
    rec.onresult = (event) => {
      if (session !== sessionRef.current) return
      heardSound = true
      deadSessionsRef.current = 0
      let transcript = ''
      let sawFinal = false
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript + ' '
        if (event.results[i].isFinal) sawFinal = true
      }
      setInterim(transcript.trim().slice(-70))
      applyTranscript(transcript, sawFinal)
    }
    rec.onerror = (e) => {
      if (session !== sessionRef.current) return
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') stopWithError('Microphone access was blocked — allow it in browser settings.')
      else if (e.error === 'audio-capture') stopWithError('No audio device captured — pick the right microphone in browser settings.')
      else if (e.error === 'language-not-supported') stopWithError(`This browser can't recognize ${rec.lang} speech.`)
      else if (e.error === 'network') setMicError('Speech service network error — retrying…')
    }
    rec.onend = () => {
      if (session !== sessionRef.current) return
      if (!listeningRef.current || completedRef.current) return
      if (!heardSound) {
        deadSessionsRef.current++
        if (deadSessionsRef.current >= 4) {
          stopWithError('Mic is open but no sound is reaching it. Click the 🔒/camera icon in the address bar, pick your real microphone, then tap the mic again.')
          return
        }
      }
      setTimeout(() => {
        if (session !== sessionRef.current || !listeningRef.current) return
        try { rec.start() } catch { /* restart raced */ }
      }, 500)
    }

    recRef.current = rec
    listeningRef.current = true
    deadSessionsRef.current = 0
    setListening(true)
    try { rec.start() } catch {
      setTimeout(() => { if (session === sessionRef.current && listeningRef.current) { try { rec.start() } catch { /* give up */ } } }, 400)
    }
  }, [targetLang, applyTranscript])

  // Recycle recognition when the challenge changes while the mic is on.
  useEffect(() => {
    if (!listeningRef.current) return
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
    const t = setTimeout(() => { if (listeningRef.current) startListening() }, 350)
    return () => clearTimeout(t)
  }, [idx]) // eslint-disable-line

  useEffect(() => () => {
    clearTimeout(advanceTimerRef.current)
    listeningRef.current = false
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
  }, [])

  const retry = () => {
    clearTimeout(advanceTimerRef.current)
    completedRef.current = false
    setHits({ forIdx: idx, states: (item?.targetTokens || []).map(() => false) })
    setInterim('')
    if (listeningRef.current) { sessionRef.current++; try { recRef.current?.abort() } catch { /* fine */ }; setTimeout(startListening, 250) }
  }

  const speakAnswer = () => { if (item) speak(item.sentence || item.conjugated, voices[targetLang] || voices['eng'], voiceSpeed) }
  const speakConjugated = () => { if (item) speak(item.conjugated, voices[targetLang] || voices['eng'], voiceSpeed) }

  // ── render: setup / loading / error ─────────────────────────────────────
  if (phase !== 'drill') {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.panel}>
          <div className={styles.modeLabel}>Verb Drill · {langName}</div>
          <h1 className={styles.title}>Conjugation practice</h1>
          <p className={styles.sub}>{verbs.length} verb{verbs.length === 1 ? '' : 's'} from this class · say the conjugated verb out loud.</p>

          {phase === 'loading' ? (
            <div className={styles.loading}>
              <div className={styles.spinner} />
              <div className={styles.loadingText}>Building {count} conjugation challenges…</div>
            </div>
          ) : (
            <>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Tense</span>
                <div className={styles.chipRow}>
                  {TENSES.map(t => (
                    <button key={t.key} className={`${styles.chip} ${tense === t.key ? styles.chipActive : ''}`} onClick={() => setTense(t.key)}>{t.label}</button>
                  ))}
                </div>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Person</span>
                <select className={styles.select} value={person} onChange={e => setPerson(e.target.value)}>
                  {PERSONS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Verb</span>
                <select className={styles.select} value={focusVerb} onChange={e => setFocusVerb(e.target.value)}>
                  <option value="">All verbs (mixed)</option>
                  {verbs.map((v, i) => <option key={i} value={v.word}>{v.word} — {v.meaning}</option>)}
                </select>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>How many per set</span>
                <div className={styles.chipRow}>
                  {[5, 10, 15].map(n => (
                    <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>
                  ))}
                </div>
              </div>
              {phase === 'error' && <div className={styles.error}>{errorMsg}</div>}
              <button className={styles.generateBtn} onClick={() => generate(false)} disabled={verbs.length === 0}>🎤 Start drill</button>
            </>
          )}
        </div>
      </div>
    )
  }

  // ── render: drill ───────────────────────────────────────────────────────
  const roman = romanizationOf(item?.sentence, targetLang)
  const showHint = attempts >= HINT_AFTER && item?.hint
  const recLangLabel = recognitionLangs[targetLang] || 'en-US'

  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={() => setPhase('setup')}><CloseIcon /></button>

      {regenerating && (
        <div className={styles.regenOverlay}><div className={styles.spinner} /><div className={styles.loadingText}>New set…</div></div>
      )}

      <div className={styles.drill}>
        <div className={styles.drillTop}>
          <span className={styles.modeLabel}>Set {setNum} · {TENSES.find(t => t.key === tense)?.label} · {languages[targetLang] || targetLang}</span>
          <span className={styles.progress}>{idx + 1} / {items.length}</span>
        </div>

        <div className={styles.card}>
          <div className={styles.subjectRow}>
            <span className={styles.subjectChip}>{item?.subjectEnglish || '—'}</span>
            {item?.subjectNative && <span className={styles.subjectNative}>{item.subjectNative}</span>}
          </div>

          <div className={styles.verbInfo}>
            conjugate <strong>{item?.verb}</strong>
            {verbs.find(v => v.word === item?.verb)?.meaning ? ` — ${verbs.find(v => v.word === item.verb).meaning}` : ''}
          </div>

          {/* stem + blank / reveal */}
          <div className={styles.blankBox}>
            {states.every(Boolean) && states.length > 0 ? (
              <span className={styles.answerGot}>{item?.conjugated}</span>
            ) : revealed ? (
              <span className={styles.answerReveal}>
                <span className={styles.stem}>{item?.stem}</span><span className={styles.ending}>{item?.ending}</span>
              </span>
            ) : (
              <span className={styles.answerBlank}>
                <span className={styles.stem}>{item?.stem}</span>
                <span className={styles.blank}>{'_'.repeat(Math.max(2, (item?.ending || '').length))}</span>
              </span>
            )}
          </div>

          <div className={styles.sentence}>{item?.sentence}</div>
          {showRoman && roman && <div className={styles.roman}>{roman}</div>}
          {item?.english && <div className={styles.english}>{item.english}</div>}

          {showHint && (
            <div className={styles.hintBox}>💡 {item.hint}</div>
          )}
          {revealed && !states.every(Boolean) && (
            <div className={styles.revealNote}>Answer: <strong>{item?.conjugated}</strong> — say it or skip ahead.</div>
          )}
        </div>

        <div className={styles.interim}>
          {micError ? micError : listening ? (interim || `mic open (${recLangLabel}) — say the verb`) : `${attempts} attempt${attempts === 1 ? '' : 's'}`}
        </div>

        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={speakConjugated} title="Hear the verb"><VolumeUpIcon /></button>
          <button className={`${styles.micBtn} ${listening ? styles.micBtnActive : ''}`} onClick={() => (listening ? stopListening() : startListening())} title={listening ? 'Stop the mic' : 'Speak the conjugation'}>
            {listening ? <MicIcon style={{ fontSize: 34 }} /> : <MicOffIcon style={{ fontSize: 34 }} />}
          </button>
          <button className={styles.sideBtn} onClick={retry} title="Retry"><ReplayIcon /></button>
          <button className={styles.sideBtn} onClick={advance} title="Skip / next"><NavigateNextIcon /></button>
        </div>

        <div className={styles.footRow}>
          <button className={styles.linkBtn} onClick={speakAnswer}>hear full sentence</button>
          {!revealed && <button className={styles.linkBtn} onClick={() => setRevealed(true)}>reveal answer</button>}
          <label className={styles.toggle}>
            <input type="checkbox" checked={showRoman} onChange={e => setShowRoman(e.target.checked)} /> romanization
          </label>
        </div>
      </div>
    </div>
  )
}
